import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, effect } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AuthService } from './app/pages/auth/auth.service';
import { LayoutService } from './app/layout/service/layout.service';
import { LoaderComponent } from './app/shared/loader/loader.component';
import { normalizeFontFamily } from './app/layout/config/font-options';
import { ProjectNameService } from './app/shared/services/project-name.service';
import { SessionFacade } from './app/core/session/session.facade';
import { FcmMessagingService } from '@/core/push-notification/fcm-messaging.service';
import { ToastModule } from 'primeng/toast';
import { AppToastComponent } from './app/shared/components/app-toast/app-toast.component';
import { ExcelErrorDialogComponent } from './app/shared/components/excel-error-dialog/excel-error-dialog.component';
import { GlobalSnackbarComponent } from './app/shared/components/global-snackbar/global-snackbar.component';

import { TranslationService } from './app/core/i18n/translation.service';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [CommonModule, RouterModule, LoaderComponent, ToastModule, AppToastComponent, ExcelErrorDialogComponent, GlobalSnackbarComponent],
    template: `
        <p-toast position="top-right" [breakpoints]="{'920px': {width: '100%', right: '0', left: '0'}}" />
        <app-toast key="fcm" />
        <app-excel-error-dialog />
        <app-global-snackbar />
        <app-loader></app-loader>
        <router-outlet></router-outlet>
    `
})
export class AppComponent implements OnInit {
    private translationService = inject(TranslationService);

    constructor() {
        effect(() => {
            const currentLangCode = this.translationService.languageCode();
            this.applyDirectionality(currentLangCode);
        });
    }

    private applyDirectionality(langCode: string) {
        try {
            const loginInfo = JSON.parse(localStorage.getItem('loginInfo') || '{}');
            const languageData = loginInfo?.languageData || [];
            const activeLang = languageData.find((l: any) => l.code === langCode);
            
            if (activeLang?.rightToLeft) {
                document.documentElement.setAttribute('dir', 'rtl');
            } else {
                document.documentElement.setAttribute('dir', 'ltr');
            }
        } catch (e) {
            document.documentElement.setAttribute('dir', 'ltr');
        }
    }
    private authService = inject(AuthService);
    private layoutService = inject(LayoutService);
    private projectNameService = inject(ProjectNameService);
    private sessionFacade = inject(SessionFacade);
    private fcmMessaging = inject(FcmMessagingService);

    ngOnInit() {
        const userDataLocal = JSON.parse(localStorage.getItem('userData') || '{}');
        setTimeout(() => {
            this.layoutService.layoutConfig.update((config) =>
                this.getUpdatedThemeConfig(config, userDataLocal)
            );
            this.applyFontFamily(normalizeFontFamily(userDataLocal?.fontFamily));
        }, 100);

        void this.projectNameService.ensureProjectNameOnReload(
            this.isBrowserReload(),
            this.sessionFacade.isAuthenticated()
        );

        void this.fcmMessaging.enableForLoggedInUser();
    }

    private getUpdatedThemeConfig(config: any, user: any) {
        return {
            ...config,
            primary: user?.primary || config.primary,
            surface: user?.surface || config.surface,
            fontFamily: normalizeFontFamily(user?.fontFamily || config.fontFamily),
            menuTheme: user?.menuTheme || config.menuTheme || 'jivonex',
            topbarTheme: user?.topbarTheme || config.topbarTheme || 'jivonex',
            darkTheme: user?.darkTheme !== undefined ? user.darkTheme : config.darkTheme,
            menuMode: user?.menuMode || config.menuMode,
            menuProfilePosition: user?.menuProfilePosition || config.menuProfilePosition,
        };
    }

    private applyFontFamily(fontFamily: string) {
        document.documentElement.style.setProperty('--primary-font', fontFamily);
    }

    private isBrowserReload(): boolean {
        const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
        const navigationEntry = navigationEntries[0];

        if (navigationEntry) {
            return navigationEntry.type === 'reload';
        }

        const legacyNavigation = performance as Performance & {
            navigation?: {
                type?: number;
            };
        };

        return legacyNavigation.navigation?.type === 1;
    }
}